import React from 'react'
import { colors } from '@resurs/midas'
import { Button } from '@resurs/midas-react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import IconButton from '../../../../../internal/IconButton'
import { getText } from '../../../../../internal/TextLabels'
import { ApplicationStatus } from '../../../../../models/application-status'
import { ApplicationType } from '../../../../../models/applicationType'
import { Authorities } from '../../../../../models/authorities'
import { PayApplyType } from '../../../../../models/payapply'
import { PaymentMethods } from '../../../../../models/payment-methods'
import * as actions from '../../../../../store/actions/pay-apply-actions'
import {
  actOnData,
  getDeep,
  requestPoller,
  setNestedValue,
} from '../../../../../util/helpers'
import isInternetExplorer from '../../../../../util/internetExplorerHelper'
import {
  getCoCustomerIdentification,
  getCustomerIdentification,
  isApproved,
  isIdentified,
  isSigned,
} from '../../../../../util/paymentModel'
import { isLimitedNetwork } from '../../../../../util/storeInfoUtils'
import { CustomerTypeModel } from '../../../models/private-models'
import PrintModalUI from '../../shared/PrintModalUI'
import { LegalCustomerPhysicalIdentification } from '../../shared/SignPage/LegalCustomer'
import { NaturalCustomerPhysicalIdentification } from '../../shared/SignPage/NaturalCustomer'
import Description from './Description'
import NumberedStep from './NumberedStep'
import { ConfirmSignedAgreement } from '../../shared/ConfirmSignedAgreement'
import {
  sendConfirmPaperAgreementSignedEvent,
  sendPrintAgreementEvent,
} from '../../../../../util/ga-helpers'

const _steps = {
  SKIP_SIGNING: 'SKIP_SIGNING',
  PRINT_AGREEMENT: 'PRINT_AGREEMENT',
  MANUAL_DEBIT: 'MANUAL_DEBIT',
  FINALIZE_APPLICATION: 'FINALIZE_APPLICATION',
}

class ApplicationSignPageAnalogUI extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      applicantId: {},
      coApplicantId: {},
      cardHolders: {
        data: [],
        valid: false,
      },
      owners: {
        data: [],
        valid: true,
      },
      showModal: false,
      poller: null,
      signedPendingPayment:
        props.result.payment &&
        props.result.status === ApplicationStatus.SIGNED,
    }
  }

  componentWillUnmount() {
    clearInterval(this.state.poller)
  }

  onFormSubmit(e, legalCustomer) {
    e.preventDefault()
    sendPrintAgreementEvent(!!this.props.result.payment)
    if (legalCustomer) {
      this.loading(
        _steps.PRINT_AGREEMENT,
        this.updateBusinessInformation().then(() => this.printAgreement()),
      )
    } else {
      if (isInternetExplorer()) {
        this.setState({ showModal: true })
      }
      this.loading(_steps.PRINT_AGREEMENT, this.printAgreement())
    }
  }

  triggerPoller() {
    if (this.state.poller) {
      clearInterval(this.state.poller)
    }

    requestPoller(
      () => this.props.updateApplication(),
      ({ status }) => status === 'APPROVED',
      5000,
      600000,
      interval => {
        this.setState({
          poller: interval,
        })
      },
    ).then(() => {
      this.props.computeNextPage()
    })
  }

  coApplicantPresent() {
    return !!(
      (this.props.data.formSpec.formValues &&
        this.props.data.formSpec.formValues['coapplicant-government-id']) ||
      this.props.result.coCustomer
    )
  }

  isBusinessInformationRequired() {
    const {
      data: {
        wizard: { wizard: customerType },
      },
      result: {
        paymentMethod: { type: paymentMethodType },
        application: { applicationType },
      },
    } = this.props
    return (
      customerType === CustomerTypeModel.LEGAL &&
      paymentMethodType === PaymentMethods.RESURS_NEW_CARD &&
      applicationType === ApplicationType.NEW_ACCOUNT
    )
  }

  async updateBusinessInformation() {
    if (!isSigned(this.props.result) && this.isBusinessInformationRequired()) {
      await this.props.updateBusinessInformation({
        owners: this.state.owners.data,
        cardHolders: this.state.cardHolders.data,
      })
    }
  }

  async printAgreement() {
    const {
      applicantId: { type: idType, reference: idRef },
      coApplicantId: { type: coIdType, reference: coIdRef },
    } = this.state

    await this.props.updatePaymentDebitAmount()
    if (!isIdentified(this.props.result)) {
      await this.props.updateIdentification({
        refnr: idRef,
        type: idType,
        corefnr: coIdRef,
        cotype: coIdType,
      })
    }
    const agreementUrl = await this.props.printAgreement()
    if (!isSigned(this.props.result)) {
      await this.props.doPaperSign({
        type: idType,
        refnr: idRef,
        typeCoapp: coIdType,
        refnrCoapp: coIdRef,
      })
    }
    if (isInternetExplorer()) {
      window.open(agreementUrl, '_blank')
    }
    this.setState({
      agreementUrl,
      showModal: true,
    })
  }

  async loading(action, promise) {
    const loaderName = `${action}Loading`
    this.setState({ [loaderName]: true })
    try {
      await promise
    } catch (ignored) {
      // ignore errors, just hide loader
    }
    this.setState({ [loaderName]: false })
  }

  isLoading(action) {
    return !!this.state[`${action}Loading`]
  }

  async updateAndClosePrintModal() {
    sendConfirmPaperAgreementSignedEvent(!!this.props.result.payment)
    await this.props.updateApplication().then(() =>
      this.setState(state => ({
        finalizeEnabled: !state.signedPendingPayment,
        showModal: false,
      })),
    )
  }

  skipSigning() {
    const {
      doSkipSigning,
      data: {
        wizard: { newPageLoading },
      },
    } = this.props
    return {
      type: _steps.SKIP_SIGNING,
      node: (
        <>
          <Description
            heading={getText('payApply.sign.skipSigningHeading')}
            text={getText('payApply.sign.skipSigningDescription')}
          />
          <div
            style={{
              float: 'right',
            }}
          >
            <Button
              id="complete-sign-btn"
              type="primary"
              onClick={() => doSkipSigning()}
              loading={newPageLoading}
            >
              {getText('payApply.sign.nextBtn')}
            </Button>
          </div>
        </>
      ),
      enabled: true,
    }
  }

  legalSigning() {
    const { result, disabled } = this.props

    const {
      applicantId: { type: idType, reference: idRef },
      cardHolders,
      owners,
    } = this.state

    return {
      type: _steps.PRINT_AGREEMENT,
      node: (
        <form onSubmit={e => this.onFormSubmit(e, true)}>
          <LegalCustomerPhysicalIdentification
            application={result}
            onChangeIdentification={data =>
              this.setState({ applicantId: data })
            }
            onChangeCardHolders={data =>
              this.setState({
                cardHolders: {
                  data: data.cardHolders,
                  valid: data.valid,
                },
              })
            }
            onChangeOwners={data =>
              this.setState({
                owners: {
                  data: data.owners,
                  valid: data.valid,
                },
              })
            }
            disabled={disabled || isSigned(result)}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              height: '100%',
              paddingTop: '2rem',
              position: 'relative',
              ...((isSigned(result) && { zIndex: 11 }) || {}),
            }}
          >
            <Button
              id="print-agreement-btn"
              form
              type="primary"
              loading={this.isLoading(_steps.PRINT_AGREEMENT)}
              disabled={
                disabled ||
                !(idRef && idType) ||
                (this.isBusinessInformationRequired() &&
                  (!cardHolders.valid || !owners.valid))
              }
            >
              {result.payment
                ? getText('payApply.sign.printPaperSignAndDebit')
                : getText('payApply.sign.printPaperSign')}
            </Button>
          </div>
        </form>
      ),
      enabled: !disabled && !isSigned(result),
    }
  }

  naturalSigning() {
    const { disabled, result, limitedNetwork } = this.props

    const {
      applicantId: { reference: idRef },
      coApplicantId: { reference: coIdRef },
      signedPendingPayment,
    } = this.state

    return {
      type: _steps.PRINT_AGREEMENT,
      heading: getText(
        result.application
          ? result.payment
            ? limitedNetwork
              ? 'payApply.sign.signTypeApplicationPayment'
              : 'payApply.sign.signTypeApplication'
            : 'payApply.sign.signTypeApplication'
          : 'payApply.sign.signTypePayment',
      ),
      node: (
        <form onSubmit={e => this.onFormSubmit(e)}>
          <NaturalCustomerPhysicalIdentification
            onChangeIdentification={data =>
              this.setState({ applicantId: data })
            }
            onChangeCoApplicantIdentification={data =>
              this.setState({ coApplicantId: data })
            }
            coApplicant={this.coApplicantPresent()}
            idInfo={{
              customer: getCustomerIdentification(result),
              coCustomer: getCoCustomerIdentification(result),
            }}
            disabled={disabled || isSigned(result)}
          >
            <div
              style={{
                display: 'flex',
                marginTop: 'auto',
                paddingLeft: '1rem',
                height: '100%',
                position: 'relative',
                ...((isSigned(result) && { zIndex: 11 }) || {}),
              }}
            >
              <Button
                id="print-agreement-btn"
                form
                loading={this.isLoading(_steps.PRINT_AGREEMENT)}
                disabled={disabled || !idRef || (result.coCustomer && !coIdRef)}
              >
                {result.payment && !signedPendingPayment
                  ? getText('payApply.sign.printPaperSignAndDebit')
                  : getText('payApply.sign.printPaperSign')}
              </Button>
            </div>
          </NaturalCustomerPhysicalIdentification>
        </form>
      ),
      enabled: !disabled && !isSigned(result),
    }
  }

  intermezzo() {
    if (this.props.result.payment && !this.props.limitedNetwork) {
      return {
        type: _steps.MANUAL_DEBIT,
        heading: getText('payApply.sign.manualDebitHeading'),
        text: getText('payApply.sign.manualDebitDescription'),
        node: (
          <>
            {actOnData(this.props.result.payment.manualDebitReference, ref => (
              <p style={{ color: colors.orange }}>
                <strong>
                  {getText('payApply.sign.manualDebitReference', ref)}
                </strong>
                <IconButton
                  blue
                  icon="midasSync2"
                  spin={!!this.state.poller}
                  style={{
                    float: 'right',
                    marginTop: '-0.6rem',
                    position: 'absolute',
                    right: '0',
                  }}
                  onClick={() => {
                    this.triggerPoller()
                  }}
                  data-cy="refresh-application"
                />
              </p>
            ))}
          </>
        ),
        enabled: isSigned(this.props.result) && !isApproved(this.props.result),
      }
    }
    if (this.state.signedPendingPayment) {
      return {
        type: _steps.MANUAL_DEBIT,
        heading: getText('payApply.sign.manualDebitHeading'),
        text: getText('payApply.sign.debitDescription'),
        node: (
          <Button
            type="primary"
            onClick={() => {
              this.setState({ finalizeEnabled: true, showModal: false })
            }}
            loading={this.props.data.wizard.newPageLoading}
            disabled={this.state.finalizeEnabled}
          >
            {getText('payApply.sign.completePayment')}
          </Button>
        ),
        enabled: true,
      }
    }
    return null
  }

  finalizeApplication() {
    const { result, onNext } = this.props

    const { finalizeEnabled, signedPendingPayment } = this.state

    if (result.payment) {
      if (!this.props.limitedNetwork) {
        if (isInternetExplorer()) {
          return {
            type: _steps.FINALIZE_APPLICATION,
            func: this.triggerPoller,
            enabled: finalizeEnabled,
          }
        }
        return {
          type: _steps.FINALIZE_APPLICATION,
          func: this.triggerPoller,
          enabled: finalizeEnabled && isSigned(result),
        }
      }
      if (isInternetExplorer()) {
        return {
          type: _steps.FINALIZE_APPLICATION,
          func: onNext,
          enabled: finalizeEnabled,
        }
      }
      return {
        type: _steps.FINALIZE_APPLICATION,
        func: onNext,
        enabled: isSigned(result) && (!signedPendingPayment || finalizeEnabled),
      }
    }
    return {
      type: _steps.FINALIZE_APPLICATION,
      func: onNext,
      enabled: finalizeEnabled && isSigned(result),
    }
  }

  signingSteps() {
    const {
      authorities,
      data: {
        wizard: { wizard: customerType },
      },
      result,
    } = this.props

    if (authorities.includes(Authorities.PAY_APPLY_SKIP_SIGNING)) {
      return [this.skipSigning()]
    }
    if (customerType === CustomerTypeModel.LEGAL && result.application) {
      return [
        this.legalSigning(),
        this.intermezzo(),
        this.finalizeApplication(),
      ]
    }
    return [
      this.naturalSigning(),
      this.intermezzo(),
      this.finalizeApplication(),
    ]
  }

  render() {
    return (
      <>
        {this.signingSteps()
          .filter(step => !!step)
          .map((step, i) => {
            if (step.func) {
              if (
                step.enabled &&
                !getDeep(this.state, `pending.${step.type}`)
              ) {
                this.setState(
                  state => setNestedValue(state, `pending.${step.type}`, true),
                  step.func,
                )
              }
              return null
            }
            return (
              <NumberedStep
                key={step.type}
                number={i + 1}
                enabled={step.enabled}
                heading={step.heading}
                text={step.text}
              >
                {step.node}
              </NumberedStep>
            )
          })}
        {isInternetExplorer() ? (
          <ConfirmSignedAgreement
            show={this.state.showModal}
            loading={this.state[`${_steps.PRINT_AGREEMENT}Loading`]}
            reopenAgreementUrl={this.state.agreementUrl}
            onClose={() => this.updateAndClosePrintModal()}
            onCancel={() => this.setState({ showModal: false })}
            isPayment={!!this.props.result.payment}
          />
        ) : (
          actOnData(this.state.agreementUrl, url => (
            <PrintModalUI
              title={
                this.props.result.payment
                  ? getText('payApply.sign.printPurchasePaperSignTitle')
                  : getText('payApply.sign.printPaperSignTitle')
              }
              show={this.state.showModal}
              url={url}
              onClose={() => this.updateAndClosePrintModal()}
              closeText={
                this.props.result.payment
                  ? this.state.signedPendingPayment
                    ? getText('default.close')
                    : getText('payApply.sign.completePayment')
                  : getText('payApply.sign.completePaperSigning')
              }
            />
          ))
        )}
      </>
    )
  }
}

ApplicationSignPageAnalogUI.propTypes = {
  data: PropTypes.object.isRequired,
  result: PropTypes.object.isRequired,
  updatePaymentDebitAmount: PropTypes.func.isRequired,
  updateBusinessInformation: PropTypes.func.isRequired,
  updateIdentification: PropTypes.func.isRequired,
  printAgreement: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  doSkipSigning: PropTypes.func.isRequired,
  doPaperSign: PropTypes.func.isRequired,
  updateApplication: PropTypes.func.isRequired,
  authorities: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  limitedNetwork: PropTypes.bool.isRequired,
  computeNextPage: PropTypes.func.isRequired,
}

ApplicationSignPageAnalogUI.defaultProps = {
  disabled: false,
}

const mapStateToProps = ({ storeInfo }) => ({
  limitedNetwork: isLimitedNetwork(storeInfo),
})

const mapDispatchToProps = (
  dispatch,
  {
    data: {
      current: { id, type },
    },
  },
) => ({
  doSkipSigning: () => {
    return dispatch(actions.skipSigning(id))
  },
  doPaperSign: idInfo => {
    return dispatch(actions.paperSign(id, idInfo))
  },
  printAgreement: () => dispatch(actions.printAgreement()),
  updateApplication: () => {
    if (type === PayApplyType.PAYMENT) {
      return dispatch(actions.getPayment(id))
    }
    return dispatch(actions.getApplication(id))
  },
  updateIdentification: idInfo => {
    return dispatch(actions.updateIdentificationStatus(idInfo))
  },
  updateBusinessInformation: businessData => {
    return dispatch(actions.updateBusinessInformation(id, businessData))
  },
  updatePaymentDebitAmount: () => {
    return dispatch(actions.updatePaymentDebitAmount)
  },
  computeNextPage: () => {
    // triggers computeNextPage in PayApply.jsx
    dispatch(actions.newPageLoading(true))
    dispatch(actions.newPageLoading(false))
  },
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ApplicationSignPageAnalogUI)
